import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ActionTypes from '@utils/deviceActions';
import rolesConstants from '@utils/rolesConstants';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import SelectCustomers from '@components/SelectCustomers';
import { Form, FormInstance, Select } from 'antd';
import { IDevice } from '@pages/Assets/Inventory/types';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import useActivationMot from './useActivationMot';

interface IContract {
  vendor: string;
  planName: string;
  assetNumber: string;
  acronym: string;
}

interface ActivationProps {
  devices: IDevice[];
  form: FormInstance<any>;
}

const useActivation = ({
  devices,
  form,
}: ActivationProps): DeviceActionProps => {
  const { t } = useTranslation([
    'actions-modal',
    'device-actions',
    'attributes',
  ]);
  const {
    selectedApp,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const isRoot = customerLogged?.type === 'ROOT';
  const formCustomerId = Form.useWatch('customerId', form);

  const customerId = useMemo(
    () => (isRoot ? formCustomerId : customerLogged?.id),
    [customerLogged?.id, formCustomerId, isRoot],
  );

  const { data: contracts } = useSwr<IContract[] | ''>(
    customerId ? '/service-proxy/contracts' : null,
    {
      params: {
        customerId,
        product: selectedApp?.toUpperCase(),
      },
    },
  );

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts?.map(({ planName, assetNumber }) => ({
            value: assetNumber,
            label: `${assetNumber} - ${planName}`,
          }))
        : [],
    [contracts],
  );

  const actionKey = useMemo(
    () =>
      selectedApp &&
      [
        ApplicationsConstants.MULTI_CONNECT,
        ApplicationsConstants.CONNECT_ONE,
      ].includes(selectedApp)
        ? ActionTypes.ACTIVATION_MULTI_CONNECT
        : ActionTypes.ACTIVATION_MOT,
    [selectedApp],
  );

  const activationMotAction = useActivationMot({ devices, form });

  const selectedAction: DeviceActionProps = useMemo(() => {
    const actions: { [key: number]: DeviceActionProps } = {
      [ActionTypes.ACTIVATION_MOT]: activationMotAction,
    };

    return actions[actionKey];
  }, [activationMotAction, actionKey]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('attributes:contract'),
        name: 'contractId',
        showWhenUpload: true,
        content: (
          <Select
            options={contractsOptions}
            disabled={!customerId || !devices || !contracts}
          />
        ),
      },
    ];

    if (isRoot) {
      defaultSteps.unshift({
        title: t('simcard-import.destination-client'),
        name: 'customerId',
        showWhenUpload: true,
        showItemMessage: contractsOptions?.length === 0,
        itemMessage: t('none-found', {
          type: t('attributes:contract')?.toLowerCase(),
        }),
        content: <SelectCustomers disabled={!devices} />,
      });
    }

    return defaultSteps;
  }, [t, contractsOptions, customerId, devices, contracts, isRoot]);

  return {
    ...selectedAction,
    title: t(`device-actions:labels.common.${rolesConstants.SIMCARD_IMPORT}`),
    steps,
  };
};

export default useActivation;
